import React from "react";
import { Image, Tag } from "antd";
import moment from "moment";

// Shared definitions for the contractor "Licenses & Certificates" tab.
//
// The same certificate list is rendered in three places (contractor portal
// profile, ContractCompanyDetailModal, ContractCompanyRelationshipTable) plus
// the FacilityKPI-Admin contractor page, so the columns live here to keep the
// four views from drifting apart.

export const CERT_TYPE_LICENSE = "license";
export const CERT_TYPE_CERTIFICATE = "certificate";

export const CERT_TYPE_OPTIONS = [
  { value: CERT_TYPE_LICENSE, label: "License" },
  { value: CERT_TYPE_CERTIFICATE, label: "Certificate" },
];

export const CERT_TYPE_LABELS = {
  [CERT_TYPE_LICENSE]: "License",
  [CERT_TYPE_CERTIFICATE]: "Certificate",
};

export const CERT_TYPE_COLORS = {
  [CERT_TYPE_LICENSE]: "blue",
  [CERT_TYPE_CERTIFICATE]: "green",
};

// Rows saved before the enum existed (or sent with an empty value) fall back to
// "certificate", mirroring the model default.
export const normalizeCertType = (value) =>
  value === CERT_TYPE_LICENSE ? CERT_TYPE_LICENSE : CERT_TYPE_CERTIFICATE;

export const getCertTypeLabel = (value) => CERT_TYPE_LABELS[normalizeCertType(value)];

export const getCertTypeColor = (value) => CERT_TYPE_COLORS[normalizeCertType(value)];

// "License number" only ever applies to a license row.
export const isLicenseRow = (record) => normalizeCertType(record?.cert_type) === CERT_TYPE_LICENSE;

export const certificateRowKey = (item) =>
  item?.id ?? `${item?.cert_name || "certificate"}-${item?.cert_type || ""}-${item?.expiration_date || ""}`;

const renderValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return <span style={{ color: "#8c8c8c" }}>{fallback}</span>;
  }
  return value;
};

export const formatCertificateDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "-";

export const renderCertificateDate = (value) => renderValue(formatCertificateDate(value));

/**
 * Read-only columns for the "Licenses & Certificates" tab.
 * Returns a fresh array on every call so callers can still spread/patch it.
 */
export const buildCertificateColumns = () => [
  {
    title: "Name",
    dataIndex: "cert_name",
    key: "cert_name",
    width: 200,
    ellipsis: true,
    render: (value) => renderValue(value),
  },
  {
    title: "Type",
    dataIndex: "cert_type",
    key: "cert_type",
    width: 120,
    render: (value) => <Tag color={getCertTypeColor(value)}>{getCertTypeLabel(value)}</Tag>,
  },
  {
    title: "License number",
    dataIndex: "license_number",
    key: "license_number",
    width: 160,
    ellipsis: true,
    render: (value, record) => (isLicenseRow(record) ? renderValue(value) : renderValue(null)),
  },
  {
    title: "Issue date",
    dataIndex: "issue_date",
    key: "issue_date",
    width: 130,
    render: renderCertificateDate,
  },
  {
    title: "Expiration date",
    dataIndex: "expiration_date",
    key: "expiration_date",
    width: 150,
    render: renderCertificateDate,
  },
  {
    title: "Issuing authority",
    dataIndex: "issuer",
    key: "issuer",
    width: 180,
    ellipsis: true,
    render: (value) => renderValue(value),
  },
  {
    title: "Original",
    dataIndex: "cert_file",
    key: "cert_file",
    width: 160,
    render: (value) =>
      value ? (
        <Image
          src={value}
          alt="License or certificate"
          width={110}
          height={110}
          style={{ objectFit: "cover", borderRadius: 4 }}
          // getContainer -> body so the preview escapes the modal's stacking
          // context; zIndex must beat the Modal's own layer (rc-dialog base 1000).
          preview={{ mask: "View", getContainer: () => document.body, zIndex: 2100 }}
        />
      ) : (
        <span style={{ color: "#8c8c8c" }}>-</span>
      ),
  },
];

export const CERTIFICATE_TABLE_SCROLL_X = 1100;

export const CERTIFICATE_TAB_TITLE = (count) =>
  `Licenses & Certificates${count ? ` (${count})` : ""}`;
